<template>
  <div class="space-y-6">
    <div class="flex items-center justify-between">
      <div>
        <h3 class="text-lg font-semibold text-gray-900 dark:text-white">Connections</h3>
        <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Reusable provider credentials for bulk model importing
        </p>
      </div>
      <Button variant="primary" @click="handleCreate">
        <svg class="w-4 h-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
        </svg>
        Add Connection
      </Button>
    </div>

    <div v-if="isLoading" class="flex items-center justify-center py-12">
      <LoadingSpinner size="lg" />
    </div>

    <Alert v-else-if="error" variant="error">{{ error }}</Alert>

    <!-- eslint-disable-next-line asab-design-system/no-raw-button-card-markup -- dashed empty-state box, not a Card (no dashed/empty variant exists yet) -->
    <div v-else-if="!connections || connections.length === 0" class="text-center py-12 bg-gray-50 dark:bg-gray-800/50 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-700">
      <h3 class="mt-2 text-sm font-medium text-gray-900 dark:text-white">No connections</h3>
      <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">Add a connection to start importing models.</p>
    </div>

    <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      <Card v-for="conn in connections" :key="conn?.slug || Math.random()" class="space-y-4">
        <div v-if="conn" class="flex items-start justify-between">
          <div>
            <h4 class="font-semibold text-gray-900 dark:text-white">{{ conn.name }}</h4>
            <code class="text-xs text-gray-500 font-mono">{{ conn.slug }}</code>
          </div>
          <span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
            {{ conn.sdk_type }}
          </span>
        </div>
        
        <div v-if="conn" class="text-xs text-gray-500 space-y-1">
          <p v-if="conn.base_url">Base URL: <span class="font-mono">{{ conn.base_url }}</span></p>
          <p>Created: {{ conn.created_at ? new Date(conn.created_at).toLocaleDateString() : 'N/A' }}</p>
        </div>

        <div v-if="conn" class="flex items-center space-x-2 pt-2">
          <Button variant="outline" size="sm" class="flex-1" @click="handleImport(conn)">
            Import Models
          </Button>
          <Button variant="outline" size="sm" @click="handleEdit(conn)">
            <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </Button>
          <Button variant="outline" size="sm" class="text-red-600 hover:text-red-700" @click="handleDelete(conn)">
            <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </Button>
        </div>
      </Card>
    </div>

    <CustomModelConnectionForm
      :open="isFormOpen"
      :edit-connection="editingConnection"
      @update:open="isFormOpen = $event"
      @created="handleCreated"
      @updated="handleUpdated"
    />

    <ImportModelsModal
      :open="isImportModalOpen"
      :connection-slug="selectedConnection?.slug || ''"
      @update:open="isImportModalOpen = $event"
      @imported="$emit('models-imported')"
    />

    <ConfirmDeleteModal
      :open="isDeleteModalOpen"
      :model="deletingConnection"
      model-type="connection"
      @update:open="isDeleteModalOpen = $event"
      @confirm="handleDeleteConfirm"
    />
  </div>
</template>

<script setup lang="ts">
  import { ref, onMounted } from 'vue';
  import { Button, LoadingSpinner, Alert, Card } from '@asabdev/asab-ui';
  import CustomModelConnectionForm from './CustomModelConnectionForm.vue';
  import ImportModelsModal from './ImportModelsModal.vue';
  import ConfirmDeleteModal from './ConfirmDeleteModal.vue';
  import { useConnections } from '../composables/useConnections';
  import type { CustomModelConnectionResponse, CreateConnectionRequest, UpdateConnectionRequest } from '../types';

  defineEmits(['models-imported']);

  const {
    connections,
    isLoading,
    error,
    fetchConnections,
    createConnection,
    updateConnection,
    deleteConnection,
  } = useConnections();

  const isFormOpen = ref(false);
  const isImportModalOpen = ref(false);
  const isDeleteModalOpen = ref(false);
  const editingConnection = ref<CustomModelConnectionResponse | null>(null);
  const deletingConnection = ref<CustomModelConnectionResponse | null>(null);
  const selectedConnection = ref<CustomModelConnectionResponse | null>(null);

  const handleCreate = () => { editingConnection.value = null; isFormOpen.value = true; };
  const handleEdit = (conn: CustomModelConnectionResponse) => { editingConnection.value = conn; isFormOpen.value = true; };
  const handleDelete = (conn: CustomModelConnectionResponse) => { deletingConnection.value = conn; isDeleteModalOpen.value = true; };
  const handleImport = (conn: CustomModelConnectionResponse) => { selectedConnection.value = conn; isImportModalOpen.value = true; };

  const handleCreated = async (data: CreateConnectionRequest) => {
    if (await createConnection(data)) isFormOpen.value = false;
  };

  const handleUpdated = async (data: UpdateConnectionRequest) => {
    if (editingConnection.value && await updateConnection(editingConnection.value.slug, data)) isFormOpen.value = false;
  };

  const handleDeleteConfirm = async () => {
    if (deletingConnection.value && await deleteConnection(deletingConnection.value.slug)) isDeleteModalOpen.value = false;
  };

  onMounted(fetchConnections);
</script>
